// src/pages/About.js
import React from 'react';

function About() {
  return (
    <div className="page">
      {/* Hero-style intro */}
      <div style={{ 
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        padding: '3rem 2rem',
        borderRadius: '20px',
        textAlign: 'center',
        marginBottom: '2rem',
        border: '1px solid rgba(42, 75, 160, 0.1)'
      }}>
        <h1 style={{ color: '#2A4BA0', marginBottom: '1rem', fontSize: '2.5rem' }}>About DC Hawks Track Club</h1>
        <p style={{ fontSize: '1.2rem', color: '#4A5568', maxWidth: '600px', margin: '0 auto' }}>
          Where young runners discover their potential, build lasting friendships, 
          and fall in love with the sport one step at a time! 🌟
        </p>
      </div>

      {/* Philosophy Cards */}
      <div className="highlights">
        <div className="highlight-card">
          <h3>🎉 Fun First</h3>
          <p>If running isn't enjoyable, something is wrong!</p>
        </div>
        
        <div className="highlight-card">
          <h3>🤝 Everyone Belongs</h3>
          <p>Every runner belongs here, regardless of speed or experience. We celebrate every achievement!</p>
        </div>
        
        <div className="highlight-card">
          <h3>🌱 Growth Mindset</h3>
          <p>Physical, mental, and social development through sport. We focus on personal improvement, not just winning.</p>
        </div>

        <div className="highlight-card">
          <h3>⚖️ Life Balance</h3>
          <p>Running enhances life. We support healthy habits that last a lifetime.</p>
        </div>
      </div>

      <div className="content-section">
        <h2>The Story Behind DC Hawks</h2>
        <p>
          DC Hawks Track Club is a youth running club based in Northwest Washington, DC. 
          It was founded by Dario Caldara, a parent of three children attending 
          DC public schools. After beginning as a volunteer coach 
          at Hardy Middle School in December 2023, Dario was inspired by the energy 
          and passion so many kids showed for running.
        </p>
        <p>
          DC Hawks was created to help young runners nurture that passion beyond the 
            school season through consistent training, racing opportunities, and lasting 
            friendships. What began as support for the Hardy track team has grown 
            into a small but active community of runners and families, united by their love of the sport
            and their commitment to helping each other up.
        </p>
        <p>
          Coach Dario still coaches at Hardy, where he maintains the school's{' '}
          <a
            href="https://dareios82.github.io/hardy-xc-track-records/index.html"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#2A4BA0', fontWeight: 'bold' }}
          >
            cross country and track record book ↗
          </a>. DC Hawks is open to runners from any school.
        </p>
        <p>
          Questions? Contact Coach Dario Caldara (
          <a href="mailto:dario.caldara@gmail.com" style={{ color: '#2A4BA0', fontWeight: 'bold' }}>
            dario.caldara@gmail.com
          </a>) or Coach Beth Ljung (
          <a href="mailto:bethmljung@gmail.com" style={{ color: '#2A4BA0', fontWeight: 'bold' }}>
            bethmljung@gmail.com
          </a>).
        </p>
      </div>

      <div className="content-section">
        <h2>Who Can Join?</h2>
        <p>
          <strong>Anyone can join DC Hawks!</strong> Though most of our athletes are in grades 4 through 8, 
          we welcome to our community all ages who share our enthusiasm for the sport and our values 
          of inclusion and fun.
        </p>
        
        <div style={{ 
          background: '#f8fafc', 
          padding: '2rem', 
          borderRadius: '12px', 
          margin: '1.5rem 0',
          border: '2px dashed #2A4BA0'
        }}>
          <h3 style={{ color: '#2A4BA0', marginBottom: '1rem' }}>All Experience Levels Welcome! 🎯</h3>
          <p style={{ marginBottom: '1rem' }}>
            Whether your child is just starting to run or is already competing at the school level, 
            DC Hawks has something to offer. Many of our members also run for their school teams 
            during the academic year, using DC Hawks as additional support and training.
          </p>
        </div>
      </div>

      <div className="content-section">
        <h2>Our Training Philosophy</h2>
        <p>
          During the school year, most of our athletes practice regularly with their school teams, 
          so DC Hawks supplements rather than replaces their primary training. Think of us as 
          your running support community!
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginTop: '2rem' }}>
          <div style={{ background: '#fff5f5', padding: '1.5rem', borderRadius: '12px', border: '1px solid #fed7d7' }}>
            <h4 style={{ color: '#c53030', marginBottom: '0.5rem' }}>🏃‍♀️ Weekend Activities</h4>
            <p style={{ fontSize: '0.9rem', margin: 0 }}>Saturday Parkruns and occasional races that bring our community together</p>
          </div>
          
          <div style={{ background: '#f0fff4', padding: '1.5rem', borderRadius: '12px', border: '1px solid #c6f6d5' }}>
            <h4 style={{ color: '#38a169', marginBottom: '0.5rem' }}>📚 Training Guidance</h4>
            <p style={{ fontSize: '0.9rem', margin: 0 }}>Tips, advice, and support for individual goals and development</p>
          </div>
          
          <div style={{ background: '#fffaf0', padding: '1.5rem', borderRadius: '12px', border: '1px solid #fbd38d' }}>
            <h4 style={{ color: '#d69e2e', marginBottom: '0.5rem' }}>👥 Community Connection</h4>
            <p style={{ fontSize: '0.9rem', margin: 0 }}>Building friendships and connections across schools and age groups</p>
          </div>
        </div>
      </div>

      <div className="content-section">
        <h2>Ready to Get Started?</h2>
        <p>
          Joining DC Hawks is easy and informal! We coordinate most activities through 
          our WhatsApp group, where families can stay updated on upcoming events, ask 
          questions, and connect with other members.
        </p>
        
        <div style={{ 
          background: 'linear-gradient(135deg, #e6fffa 0%, #f0fff4 100%)',
          padding: '2rem',
          borderRadius: '12px',
          margin: '1.5rem 0',
          textAlign: 'center'
        }}>
          <h3 style={{ color: '#2A4BA0', marginBottom: '1rem' }}>🌟 Best First Step</h3>
          <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>
            Join us for a Saturday Parkrun at Fletcher's Cove! It's free, fun, and gives 
            you a perfect taste of our community culture.
          </p>
          <p style={{ fontSize: '0.9rem', color: '#4A5568', margin: 0 }}>
            From there, you can decide how much you'd like to participate.
          </p>
        </div>
      </div>
    </div>
  );
}

export default About;